// The season and the holiday themes on a Joutseno calendar date. Pure, so it can be unit tested.
// The season follows typical South Karelia dates (snow, lake ice, birch leaves), not the weather.

/** A special theme the stadium dresses up for. */
export type ThemeId =
  | 'newYear' | 'valentine' | 'easter' | 'vappu' | 'midsummer' | 'catDay' | 'halloween' | 'independence' | 'christmas';

export interface SeasonState {
  /** Snow on the ground, 0 (none) to 1 (full winter cover). */
  snow: number;
  /** Ice on the lake, 0 (open water) to 1 (frozen). */
  lakeIce: number;
  /** Leaves on the birches, 0 (bare) to 1 (full). */
  leaves: number;
  /** Autumn colour of the birch leaves, 0 (green) to 1 (yellow and orange). */
  autumn: number;
  /** Fresh light-green spring leaves, 0 to 1. */
  spring: number;
  /** Dry, faded grass, 0 (lush summer green) to 1 (straw after the snow melts). */
  dryGrass: number;
  /** Leaves falling from the birches, 0 to 1. */
  leafFall: number;
  /** Snow falling today, 0 to 1. */
  snowfall: number;
  /** Pollen and seed fluff in the air, 0 to 1. */
  pollen: number;
  /** Flowers in the planters and urns. */
  flowers: boolean;
}

/** Day of the year, 0-based, for a calendar date (month 1..12). */
export function dayOfYear(year: number, month: number, day: number): number {
  return Math.round((Date.UTC(year, month - 1, day) - Date.UTC(year, 0, 1)) / 86_400_000);
}

/** Linear ramps through (month, day, value) keys within a year; wraps over New Year. */
function yearCurve(keys: [number, number, number][], year: number, doy: number): number {
  const pts = keys.map(([m, d, v]) => [dayOfYear(year, m, d), v] as const);
  const first = pts[0]!, last = pts[pts.length - 1]!;
  const len = dayOfYear(year, 12, 31) + 1;
  const all = [[last[0] - len, last[1]] as const, ...pts, [first[0] + len, first[1]] as const];
  for (let i = 1; i < all.length; i++) {
    const [d0, v0] = all[i - 1]!, [d1, v1] = all[i]!;
    if (doy <= d1) return d1 === d0 ? v1 : v0 + (v1 - v0) * (doy - d0) / (d1 - d0);
  }
  return last[1];
}

/** A small hash of a date, 0..1, so a day's weather is the same all day and on every load. */
function dayHash(year: number, doy: number): number {
  let h = Math.imul(year * 374761393 + doy * 668265263, 0x27d4eb2d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

/** The season on a Joutseno date. */
export function seasonOn(year: number, month: number, day: number): SeasonState {
  const doy = dayOfYear(year, month, day);
  const snow = yearCurve([[3, 25, 1], [4, 15, 0], [11, 15, 0], [12, 5, 1]], year, doy);
  const christmasDays = month === 12 && day >= 24 && day <= 26;
  return {
    snow,
    lakeIce: yearCurve([[4, 20, 1], [5, 5, 0], [12, 1, 0], [12, 20, 1]], year, doy),
    leaves: yearCurve([[5, 8, 0], [5, 25, 1], [9, 25, 1], [10, 25, 0]], year, doy),
    autumn: yearCurve([[5, 8, 0], [8, 25, 0], [9, 28, 1], [12, 31, 1]], year, doy),
    spring: yearCurve([[5, 1, 0], [5, 8, 1], [6, 20, 0]], year, doy),
    dryGrass: yearCurve([[1, 1, 0.8], [4, 15, 1], [5, 25, 0], [8, 15, 0], [9, 15, 0.25], [10, 25, 0.6], [11, 20, 0.8]], year, doy),
    leafFall: yearCurve([[9, 18, 0], [10, 3, 1], [10, 25, 0.6], [11, 6, 0]], year, doy),
    snowfall: snow > 0.3 && (christmasDays || dayHash(year, doy) < 0.4) ? Math.min(1, 0.5 + snow * 0.5) : 0,
    pollen: yearCurve([[5, 15, 0], [6, 1, 1], [8, 15, 1], [9, 1, 0]], year, doy),
    flowers: doy >= dayOfYear(year, 5, 15) && doy <= dayOfYear(year, 10, 10),
  };
}

/** Easter Sunday (month, day) of a year: the Gregorian computus. */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  return { month, day: ((h + l - 7 * m + 114) % 31) + 1 };
}

/** The first date from (month, from) onwards that falls on a weekday (0 = Sunday). */
function firstWeekday(year: number, month: number, from: number, weekday: number): number {
  const wd = new Date(Date.UTC(year, month - 1, from)).getUTCDay();
  return from + ((weekday - wd + 7) % 7);
}

/** Midsummer Eve: the Friday between 19 and 25 June. */
export function midsummerEve(year: number): number { return firstWeekday(year, 6, 19, 5); }

/** All Saints' Day in Finland: the Saturday between 31 October and 6 November, as a day of the year. */
export function allSaintsDay(year: number): number {
  const oct31 = dayOfYear(year, 10, 31);
  return oct31 + (firstWeekday(year, 10, 31, 6) - 31);
}

/** First Sunday of Advent: the Sunday between 27 November and 3 December, as a day of the year. */
export function firstAdvent(year: number): number {
  const nov27 = dayOfYear(year, 11, 27);
  return nov27 + (firstWeekday(year, 11, 27, 0) - 27);
}

/** The themes on a Joutseno local date and hour, in the order they are listed in ThemeId. */
export function themesOn(year: number, month: number, day: number, hour = 12): ThemeId[] {
  const doy = dayOfYear(year, month, day);
  const out: ThemeId[] = [];
  // New Year's fireworks: New Year's Eve and the small hours of New Year's Day.
  if ((month === 12 && day === 31) || (month === 1 && day === 1 && hour < 6)) out.push('newYear');
  if (month === 2 && day === 14) out.push('valentine');
  const easter = easterSunday(year), e = dayOfYear(year, easter.month, easter.day);
  if (doy >= e - 7 && doy <= e + 1) out.push('easter'); // Palm Sunday to Easter Monday
  if ((month === 4 && day === 30) || (month === 5 && day === 1)) out.push('vappu');
  if (month === 6 && (day === midsummerEve(year) || day === midsummerEve(year) + 1)) out.push('midsummer');
  if (month === 8 && day === 8) out.push('catDay'); // International Cat Day
  if (doy >= dayOfYear(year, 10, 24) && doy <= allSaintsDay(year)) out.push('halloween');
  if (month === 12 && day === 6) out.push('independence');
  if (doy >= firstAdvent(year) || (month === 1 && day <= 6)) out.push('christmas'); // to Epiphany
  return out;
}
